import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, X, Calendar as CalendarIcon } from "lucide-react";
import { displayTime } from "@/lib/format";

/**
 * NotificationCenter — polls `/notifications/upcoming` every 60s.
 *  - Fires a browser Notification (system-level, requires permission) AND
 *    plays a short chime when a NEW appointment enters the window, or when
 *    an appointment crosses the 15-min or 5-min threshold.
 *  - Also renders an in-app bell popover with the current list.
 *
 * Sound: soft two-tone chime generated on demand via the Web Audio API.
 * No external asset needed — works offline. Modern browsers require a
 * prior user gesture before audio plays; the bell click warms up the
 * AudioContext so subsequent chimes always ring.
 */

let _audioCtx = null;
function getAudioCtx() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!_audioCtx) _audioCtx = new Ctx();
    if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
    return _audioCtx;
  } catch { return null; }
}

function playChime() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    // Two tones: 880Hz then 1320Hz, ~140ms each, gentle sine — pleasant hospital chime.
    [[880, 0], [1320, 0.16]].forEach(([freq, delay]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, now + delay);
      g.gain.exponentialRampToValueAtTime(0.35, now + delay + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.32);
      o.start(now + delay);
      o.stop(now + delay + 0.35);
    });
  } catch { /* silent */ }
}

function requestPermissionOnce() {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    Notification.requestPermission().catch(() => {});
  }
}

export default function NotificationCenter() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const seenBookings = useRef(new Set());   // appt ids we've already alerted for
  const alerted15 = useRef(new Set());
  const alerted5  = useRef(new Set());
  const timerRef  = useRef(null);

  const fireDesktop = (title, body) => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        // eslint-disable-next-line no-new
        new Notification(title, { body, tag: "hc-appt", silent: true });
      }
    } catch { /* ignore */ }
  };

  const poll = async () => {
    try {
      const { data } = await api.get("/notifications/upcoming");
      const list = data.items || [];
      setItems(list);
      for (const a of list) {
        const min = a.minutes_until;
        // First-time we see this appointment in the window → treat as "just booked / arriving".
        if (!seenBookings.current.has(a.id)) {
          seenBookings.current.add(a.id);
          if (min > 15) {
            playChime();
            fireDesktop("New appointment booked", `${a.patient_name} · ${displayTime(a.time)}`);
          }
        }
        // 15-min chime.
        if (min <= 15 && min > 5 && !alerted15.current.has(a.id)) {
          alerted15.current.add(a.id);
          playChime();
          fireDesktop(`Appointment in 15 min`, `${a.patient_name} · ${displayTime(a.time)}`);
        }
        // 5-min chime (louder — plays chime twice).
        if (min <= 5 && !alerted5.current.has(a.id)) {
          alerted5.current.add(a.id);
          playChime(); setTimeout(playChime, 500);
          fireDesktop(`Appointment now / in 5 min`, `${a.patient_name} · ${displayTime(a.time)} · ${a.mode}`);
        }
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    requestPermissionOnce();
    poll();
    timerRef.current = setInterval(poll, 60_000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = items.length;
  return (
    <div className="relative" data-testid="notif-center">
      <button
        onClick={() => { getAudioCtx(); setOpen((v) => !v); }}
        className="relative w-9 h-9 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell size={16} className="text-[#47554D]" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#B14A2A] text-white text-[10px] font-bold flex items-center justify-center">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[320px] bg-white border border-[#EFECE5] rounded-2xl shadow-lg z-50" data-testid="notif-panel">
          <div className="flex items-center justify-between p-3 border-b border-[#EFECE5]">
            <div className="text-sm font-semibold text-[#1F2A24]">Upcoming appointments</div>
            <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"><X size={13} /></button>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#8A9992]">Nothing coming up in the next 20 minutes.</div>
            ) : items.map((a) => (
              <div key={a.id} className="p-3 border-b border-[#EFECE5] last:border-b-0 flex items-center gap-3" data-testid={`notif-item-${a.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#FCE7D9] text-[#5C3A21] flex items-center justify-center flex-none">
                  <CalendarIcon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1F2A24] truncate">{a.patient_name}</div>
                  <div className="text-[11px] text-[#8A9992] truncate">{displayTime(a.time)} · {a.mode}</div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-widest ${
                  a.minutes_until <= 5 ? "bg-[#FCE7D9] text-[#B14A2A]" : a.minutes_until <= 15 ? "bg-[#FFF3D6] text-[#8A5A34]" : "bg-[#E6F0EC] text-[#0A4C36]"
                }`}>
                  {a.minutes_until <= 0 ? "Now" : `${a.minutes_until}m`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
